import { Box, Button, Grid, InputAdornment, Skeleton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import CardFrom, { CardFormConfig } from '../../../component/card/card-from';
import i18n from 'i18next';
import { Staff } from '../../../models/staff.entity';
import { useManualRemove } from '../../mobx-objs/mobx-manager';
import { GroupAccountState } from './group-account-state';
import GlobalNotification from '../../../component/common/snackbar';
import { isFiscalCodeValidForCopy } from '../tax-code-copy';
// scss
import './scss/group-account.style.scss';
import ButtonComponent from '../../../component/button/button.component';
import { useTranslation } from 'react-i18next';
import CardView from '../../../component/card/card-view';
import BindAccount from '../../../component/bind-account/bind-account.component';
import { TableRowFeatures } from '../../../component/table/table.component';
import DeleteDialogComponent from '../../../component/dialog/delete-dialog.component';
import {
    ArchiveFormWorkbench,
    calculateArchiveFormProgress,
    type ArchiveFormSectionRule,
} from '../../../component/archive-form';

/** 集团账号表单支持的展示模式。 */
export type GroupAccountComponentType = 'EDIT' | 'PREVIEW' | 'ADD';

/** 集团账号卡片配置，顺序与建档导航保持一致。 */
export const GroupAccountCardArr: CardFormConfig[] = [
    {
        key: 'main',
        title: 'create_group_component_text_group_basic_information',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                comType: 'AVATAR',
                key: 'avatar',
                xs: 12,
                md: 2,
                avatarSize: 64,
                // 64px 建档图位：空态与悬浮操作按钮按原型缩小
                avatarCompact: true,
            },
            {
                key: 'nickname',
                label: 'group_management_page_text_group_name',
                required: true,
                comType: 'INPUT',
                regularType: 'inputLimit120',
                inputType: 'INPUT',
                xs: 12,
                md: 10,
            }
        ]
    },
    {
        key: 'tax',
        title: 'create_group_component_text_tax_identity',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'tax_optional_hint',
                comType: 'CUSTOMCHILDREN',
                xs: 12,
                md: 12,
                customSubChildren: (
                    <Typography variant='body2' color='text.secondary'>
                        {i18n.t('create_group_component_text_tax_optional_hint')}
                    </Typography>
                ),
            },
            {
                key: 'reg_fiscal_code',
                label: 'create_group_component_text_fiscal_code',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'fiscalCode',
                xs: 12,
                md: 4,
                required: false,
            },
            {
                key: 'reg_vat_number',
                label: 'create_group_component_text_vat_number',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'vatNumber',
                xs: 12,
                md: 4,
                required: false,
            },
            {
                key: 'reg_des_code',
                label: 'create_group_component_text_electronic_invoice_recipient_code',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'recipientCode',
                xs: 12,
                md: 4,
                required: false,
            },
            {
                key: 'reg_pec',
                label: 'create_group_component_text_pec_email',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'email',
                xs: 12,
                md: 12,
                required: false,
            },
        ]
    },
    {
        key: 'address',
        title: 'create_group_component_text_registered_address',
        rowSpacing: 6,
        columnSpacing: { md: 4, xs: 4 },
        children: [
            {
                key: 'local',
                label: 'address_component_text_address',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 8,
            },
            {
                key: 'home_number',
                label: 'address_component_text_doorbell',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4,
            },
            {
                key: 'postcode',
                label: 'address_component_text_postal_code',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4,
            },
            {
                key: 'city',
                label: 'address_component_text_city',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 4,
            },
            {
                key: 'state',
                label: 'address_component_text_province',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 2,
            },
            {
                key: 'country',
                label: 'address_component_text_country',
                inputType: 'INPUT',
                comType: 'INPUT',
                xs: 12,
                md: 2,
            },
        ]
    },
    {
        key: 'contactWay',
        title: 'create_group_component_text_contact',
        rowSpacing: 5,
        columnSpacing: { md: 4 },
        children: [
            {
                key: 'email',
                label: 'general_text_email',
                comType: 'INPUT',
                inputType: 'INPUT',
                regularType: 'email',
                xs: 12,
                md: 6,
            },
            {
                key: 'phone_prefix',
                label: 'general_text_country_code',
                comType: 'INPUT',
                inputType: 'COUNTRY_SELECT',
                xs: 12,
                md: 2,
            },
            {
                key: 'tel',
                label: 'create_group_component_text_phone',
                inputType: 'INPUT',
                comType: 'INPUT',
                regularType: 'phone',
                xs: 12,
                md: 4,
                required: false,
            },
        ]
    },
    {
        key: 'tableCom',
        title: 'responsible_person_info_component_responsible_person_info',
        rowSpacing: 6,
        xs: 12,
        md: 12,
        titleTypographyProps: {
            // height: 50
            paddingRight: 10
        },
        children: [
            {
                key: 'table',
                comType: 'TABLE',
                xs: 12,
            }
        ]
    },

];

/** 各集团建档分区的信息来源。 */
const GROUP_ARCHIVE_SECTION_SOURCES: Record<string, ArchiveFormSectionRule['source']> = {
    main: 'SALES',
    tax: 'DOCUMENT',
    address: 'DOCUMENT',
    contactWay: 'SALES',
    tableCom: 'SALES',
};

/**
 * 生成当前模式的集团建档分区规则。
 * @param componentType 集团表单展示模式
 * @returns 按工作台展示顺序排列的分区规则
 */
export function getGroupArchiveSections(
    componentType: GroupAccountComponentType = 'ADD',
): ArchiveFormSectionRule[] {
    return GroupAccountCardArr
        .filter((section) => componentType !== 'ADD' || section.key !== 'tableCom')
        .map((section) => ({
            key: section.key,
            title: typeof section.title === 'string' ? section.title : section.key,
            source: GROUP_ARCHIVE_SECTION_SOURCES[section.key],
            fields: (section.children || []).map((field) => ({
                key: field.key,
                label: field.label || field.key,
                required: field.required ?? false,
                regularType: field.regularType,
                hidden: field.hidden,
                disabled: componentType === 'PREVIEW',
            })),
        }));
}

/**
 * 集团账号信息详情
 */
const GroupAccount = observer((props: AccountProps) => {
    useTranslation();

    const { componentType } = props;
    // 当前集团表单展示模式，未传时按新建集团处理。
    const currentComponentType = componentType || 'ADD';
    // 数据管理
    const useMobxData = props.useGroupAccountMobx;

    // 骨架屏控制
    const [skeleton, setSkeleton] = React.useState<boolean>(true);


    React.useEffect(() => {
        initComponent();
        // 卸载
        return () => {
            // MobxObjectsManager.remove(GroupAccountState);
        }
    }, []);

    // 初始化组件
    const initComponent = async () => {
        await useMobxData.init();
        // 关闭骨架屏
        setSkeleton(false);
    }
    // unmount
    useManualRemove(GroupAccountState);

    // 数据
    const {
        // isEnableSkeleton,
        groupEditDetail,
        disabledBtn,
        isEditInput,
        dialogConfig,
        deleteDialogConfig,
        operateMap,
        originGroupData,
        isEmptyVerify
    } = useMobxData;

    // 当前模式可见的建档分区规则
    const archiveSectionRules = getGroupArchiveSections(currentComponentType);
    // 当前集团表单的必填完成度与格式错误
    const archiveProgress = calculateArchiveFormProgress(
        archiveSectionRules,
        groupEditDetail as Record<string, unknown>,
    );
    // 卡片组渲染数据
    const cardArr: CardFormConfig[] = GroupAccountCardArr
        .filter((parentItem) => currentComponentType !== 'ADD' || parentItem.key !== 'tableCom')
        .map((parentItem) => {
        let extraParentConfig = {};
        switch (parentItem.key) {
            case 'tableCom':
                extraParentConfig = {
                    actionRender: (
                        currentComponentType === 'PREVIEW' ? <></> :
                            <Box sx={{ marginRight: 2 }}>
                                <ButtonComponent
                                    variant='contained'
                                    size='small'
                                    // extraButtonStyle={'iconButton'}
                                    handleClick={async () => {
                                        useMobxData.setDialogConfig({
                                            visible: true,
                                        })
                                    }}
                                    action={'ADD'}
                                    titleText={i18n.t('general_text_new')}
                                />
                            </Box>
                    ),
                }
                break;
            default:
                break;
        }
        return {
            ...parentItem,
            ...extraParentConfig,
            children: parentItem?.children?.map((childrenItem) => {
                let extraConfig = {};
                switch (childrenItem.key) {
                    case 'avatar':
                        extraConfig = {
                            isDisabledUpload: currentComponentType === 'PREVIEW',
                            handleChangeAvatar: ((_avatarUrl: string, file: File) => {
                                return useMobxData.onChangeAvatar(file);
                            })
                        }
                        break;
                    case 'reg_vat_number':
                        extraConfig = {
                            InputProps: {
                                endAdornment: currentComponentType === 'PREVIEW' ? undefined : (
                                    <InputAdornment position='end'>
                                        <Button
                                            size='small'
                                            onClick={() => {
                                                const fiscalCode = groupEditDetail.reg_fiscal_code || '';
                                                if (!isFiscalCodeValidForCopy(fiscalCode)) {
                                                    GlobalNotification.error(i18n.t('archive_form_group_same_fiscal_code_invalid'));
                                                    return;
                                                }
                                                useMobxData.handleChangeText(fiscalCode, 'reg_vat_number');
                                            }}
                                        >
                                            {i18n.t('archive_form_group_same_fiscal_code', {
                                                defaultValue: '同税务注册号',
                                            })}
                                        </Button>
                                    </InputAdornment>
                                ),
                            },
                        }
                        break;
                    case 'table':
                        extraConfig = {
                            tableConfig: {
                                showChecked: currentComponentType === 'PREVIEW' ? 0 : 1,
                                headCellList:
                                    currentComponentType === 'PREVIEW' ?
                                        [
                                            {
                                                id: '1', label: i18n.t('responsible_person_info_component_text_name'),
                                                renderFunction: (row: Staff) => {
                                                    return <>{row.first_name}{row.last_name}</>
                                                }
                                            },
                                            { id: 'email', label: i18n.t('general_text_email') },
                                            { id: 'phone', label: i18n.t('general_text_phone') },

                                        ]
                                        :
                                        [
                                            {
                                                id: '1', label: i18n.t('responsible_person_info_component_text_name'),
                                                renderFunction: (row: Staff) => {
                                                    return <>{row.first_name}{row.last_name}</>
                                                }
                                            },
                                            { id: 'email', label: i18n.t('general_text_email') },
                                            {
                                                id: 'phone', label: i18n.t('general_text_phone'),
                                                renderFunction: (row: Staff) => {
                                                    return <>{row?.phone_prefix} {row?.phone}</>
                                                },
                                            },
                                            {
                                                id: '5', label: '',
                                                renderFunction: (row: Staff) => {
                                                    return renderFeatures(row   )
                                                }
                                            }
                                        ],
                                list: originGroupData.supervisor || [],
                                handleDeleteAll: async (selected: Staff[]) => {
                                    // 请求删除
                                    await useMobxData.handleDeleteBindAccount(selected, true);
                                },
                            }
                        }
                        break;
                    default:
                        break;
                }
                return {
                    ...childrenItem,
                    ...extraConfig,
                    // 非空校验
                    isEmpty: isEmptyVerify,
                    // 编辑禁用
                    disabledInput: currentComponentType === 'PREVIEW' ? true : isEditInput,
                    // 编辑反馈
                    inputIsEdited: operateMap.get(childrenItem.key) !== undefined ? true : false,
                    onChange: (e) => {
                        return useMobxData.handleChangeText(e.target.value, childrenItem.key);
                    },
                    handleChangeCountry: (value) => {
                        console.log(value);
                        return useMobxData.handleChangeText(value, 'phone_prefix');
                    }
                }
            })
        }
    }) as CardFormConfig[];

    /** 建档来源对应的用户可见说明。 */
    const sourceLabels: Record<ArchiveFormSectionRule['source'], string> = {
        SALES: i18n.t('archive_form_source_sales', { defaultValue: '销售信息' }),
        DOCUMENT: i18n.t('create_group_component_text_source_visura'),
        INTERNAL: i18n.t('archive_form_source_internal', { defaultValue: '系统信息' }),
    };

    // 工作台分区将每张既有 CardFrom 卡片接入导航与完成度
    const workbenchSections = cardArr.map((sectionConfig) => {
        // 当前卡片对应的分区规则与剩余必填数
        const sectionRule = archiveSectionRules.find((section) => section.key === sectionConfig.key)!;
        const sectionProgress = archiveProgress.sections.find((section) => section.key === sectionConfig.key);
        return {
            key: sectionConfig.key,
            title: i18n.t(sectionRule.title),
            // 来源说明并入卡片标题，避免在卡片外重复占据一行。
            sourceLabel: '',
            pendingRequired: sectionProgress?.pendingRequired ?? 0,
            content: (
                <CardFrom
                    config={[{
                        ...sectionConfig,
                        // 工作台已按分区逐张渲染，卡片必须占满整行。
                        xs: 12,
                        md: 12,
                        rowSpacing: 3,
                        columnSpacing: { xs: 3, md: 3 },
                        title: (
                            <Box component='span' className='group-account-section-title'>
                                <Typography component='span' variant='subtitle1'>
                                    {i18n.t(sectionRule.title)}
                                </Typography>
                                <Typography component='span' variant='caption' color='text.secondary'>
                                    {sectionRule.key === 'tableCom'
                                        ? i18n.t('create_group_component_text_source_person')
                                        : sourceLabels[sectionRule.source]}
                                </Typography>
                            </Box>
                        ),
                    }]}
                    data={groupEditDetail as {} || {}}
                />
            ),
        };
    });

    // *********************
    // Business Function
    // *********************

    // 功能键
    const renderFeatures = (row: Staff): React.ReactNode => {
        return (
            <TableRowFeatures
                buttons={[
                    {
                        variant: 'contained',
                        color: 'secondary',
                        action: 'DELETE',
                        handleClick: () => {
                            useMobxData.updateDeleteDialogConfig({
                                visible: true,
                                forbidConfirm: 0,
                                title: i18n.t('general_text_tips'),
                                content: i18n.t('general_text_whether_to_delete_the_selection'),
                                handleClick: async () => {
                                    if (row._id) {
                                        // 请求删除
                                        await useMobxData.handleDeleteBindAccount(row, false);
                                    }
                                }
                            })
                        }
                    }
                ]}
            />
        )
    }

    // 渲染编辑模式底部保存操作
    const renderActionBtn = () => {
        return (
            <ButtonComponent
                size='small'
                variant={'contained'}
                forbidConfirm={disabledBtn}
                iconBtn={false}
                extraTitleStyle={'button-icon'}
                titleText={`${i18n.t('job_settings_save')}`}
                handleClick={async () => {
                    await useMobxData.onSave('EDIT');
                }}
            >
                {i18n.t('job_settings_save')}
            </ButtonComponent>
        )
    }

    return (
        <Box className={`group-account-page${currentComponentType === 'ADD' ? ' group-account-page--add' : ''}`}>
            {/* 骨架屏,排除资源回收站跳转 */}
            {
                skeleton ? (
                    <SkeletonViewComp componentType={currentComponentType} />
                ) : (
                    <Box className={'contain'}>
                        <ArchiveFormWorkbench
                            sections={workbenchSections}
                            progress={archiveProgress}
                            actions={currentComponentType === 'EDIT' ? renderActionBtn() : null}
                            disabled={Boolean(disabledBtn)}
                            fixedSaveProgress={currentComponentType === 'EDIT'}
                        />
                    </Box>
                )
            }
            {/* 弹窗新增绑定负责人 绑定账号组件 */}
            {
                // 新增不显示
                currentComponentType !== 'ADD' &&
                <BindAccount
                    // 弹窗控制
                    configDialog={{
                        visible: dialogConfig.visible,
                        title: dialogConfig?.title ?? '',
                        onClose: dialogConfig?.onClose
                    }}
                    // 弹窗配置数据
                    dialogConfigData={{
                        type: 'GROUP',
                        groupStringId: useMobxData.belongedGroup ?? '',
                        restaurantStringId: '',
                        bindCallBack: async () => {
                            await useMobxData.getGroupAccountData();
                        }
                    }}
                />
            }

            {/* 是否删除所选择项目 */}
            <DeleteDialogComponent
                {...{
                    title: deleteDialogConfig.title,
                    open: deleteDialogConfig.visible,
                    onCancel: () => {
                        useMobxData.updateDeleteDialogConfig({
                            visible: false,
                            title: '',
                            content: '',
                        });
                    },
                    // 确认删除
                    onConfirm: async () => { await deleteDialogConfig?.handleClick?.(); },
                }}
            />
        </Box>
    )
});

export default GroupAccount;

type AccountProps = {
    // 集团_id
    belongedGroupId?: string;
    // 集团string_id
    belongedGroup?: string;
    // 组件类型  预览模式(用于资源回收站浏览) | 可编辑模式
    componentType?: GroupAccountComponentType;
    // 数据管理
    useGroupAccountMobx: GroupAccountState;
}

/**
 * 渲染与新建档工作台结构一致的集团表单骨架屏。
 * @param fixedSaveProgress - 编辑模式是否预留固定保存底栏
 * @returns 集团建档页面加载中的占位结构
 */
function SkeletonViewComp({ componentType }: { componentType?: GroupAccountComponentType }): JSX.Element {
    // 未传模式时与实际建档规则一致，按新增模式展示。
    const currentType = componentType || 'ADD';
    // 新增模式不包含负责人分区，编辑模式需要固定保存底栏。
    const showResponsible = currentType !== 'ADD';
    const fixedSaveProgress = currentType === 'EDIT';
    // 骨架固定底栏的内容区边界，避免覆盖左侧导航。
    const skeletonRef = React.useRef<HTMLDivElement>(null);
    const [fixedSaveProgressStyle, setFixedSaveProgressStyle] = React.useState<React.CSSProperties>();

    React.useLayoutEffect(() => {
        if (!fixedSaveProgress || typeof window === 'undefined') {
            return undefined;
        }

        // 固定底栏跟随最近内容页的水平边界。
        const contentPage = skeletonRef.current?.closest('.content-page');
        if (!contentPage) {
            return undefined;
        }

        // 同步内容页尺寸，适配侧栏展开、收起和窗口缩放。
        const syncFixedSaveProgressBoundary = () => {
            const { left, width } = contentPage.getBoundingClientRect();
            setFixedSaveProgressStyle({ left, width });
        };

        syncFixedSaveProgressBoundary();
        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(syncFixedSaveProgressBoundary);
        resizeObserver?.observe(contentPage);
        window.addEventListener('resize', syncFixedSaveProgressBoundary);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', syncFixedSaveProgressBoundary);
        };
    }, [fixedSaveProgress]);

    return (
        <Box
            ref={skeletonRef}
            className={`skeleton archive-form-skeleton${fixedSaveProgress ? ' archive-form-skeleton--fixed' : ''}`}
        >
            <Box className='archive-form-skeleton__anchors'>
                {[96, 78, 78, 72, ...(showResponsible ? [88] : [])].map((width, index) => (
                    <Skeleton
                        key={`${width}-${index}`}
                        variant='rounded'
                        width={width}
                        height={32}
                        animation='wave'
                    />
                ))}
            </Box>
            <Grid container rowSpacing={2}>
                <Grid item xs={12}>
                    <SkeletonSectionCard>
                        <Grid container spacing={2} alignItems='center'>
                            <Grid item xs={12} sm={2}>
                                <Skeleton variant='rounded' width={64} height={64} animation='wave' />
                            </Grid>
                            <Grid item xs={12} sm={10}>
                                <Skeleton variant='rounded' height={46} animation='wave' />
                            </Grid>
                        </Grid>
                    </SkeletonSectionCard>
                </Grid>
                <Grid item xs={12}>
                    <SkeletonSectionCard hint>
                        <SkeletonFieldGrid spans={[4, 4, 4, 12]} />
                    </SkeletonSectionCard>
                </Grid>
                <Grid item xs={12}>
                    <SkeletonSectionCard>
                        <SkeletonFieldGrid spans={[8, 4, 4, 4, 2, 2]} />
                    </SkeletonSectionCard>
                </Grid>
                <Grid item xs={12}>
                    <SkeletonSectionCard>
                        <SkeletonFieldGrid spans={[6, 2, 4]} />
                    </SkeletonSectionCard>
                </Grid>
                {showResponsible ? (
                    <Grid item xs={12}>
                        <SkeletonSectionCard>
                            <Skeleton variant='rounded' height={170} animation='wave' />
                        </SkeletonSectionCard>
                    </Grid>
                ) : null}
            </Grid>
            {fixedSaveProgress ? (
                <Box className='archive-form-skeleton__savebar' style={fixedSaveProgressStyle}>
                    <Skeleton variant='circular' width={30} height={30} animation='wave' />
                    <Skeleton variant='rounded' width={180} height={24} animation='wave' />
                    <Box className='archive-form-skeleton__savebar-actions'>
                        <Skeleton variant='rounded' width={88} height={36} animation='wave' />
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}

/**
 * 渲染单个建档分区卡片的骨架外壳。
 * @param hint - 是否显示分区说明占位
 * @param children - 分区字段占位内容
 * @returns 带标题和内容占位的卡片
 */
function SkeletonSectionCard({
    hint = false,
    children,
}: {
    hint?: boolean;
    children: React.ReactNode;
}): JSX.Element {
    return (
        <CardView
            className='archive-form-skeleton__card'
            contentRender={(
                <Grid container rowSpacing={2}>
                    <Grid item xs={12}>
                        <Skeleton variant='rounded' width={150} height={24} animation='wave' />
                    </Grid>
                    {hint ? (
                        <Grid item xs={12}>
                            <Skeleton variant='rounded' width='72%' height={18} animation='wave' />
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        {children}
                    </Grid>
                </Grid>
            )}
        />
    );
}

/**
 * 渲染表单字段网格占位。
 * @param count - 当前分区需要展示的字段数量
 * @returns 响应式字段占位网格
 */
function SkeletonFieldGrid({ spans }: { spans: readonly number[] }): JSX.Element {
    return (
        <Grid container spacing={2}>
            {spans.map((span, index) => (
                <Grid item xs={12} md={span} key={`${span}-${index}`}>
                    <Skeleton variant='rounded' height={46} animation='wave' />
                </Grid>
            ))}
        </Grid>
    );
}
